// Breed Matcher class for Pet Breed Matcher tool
// Handles the matching algorithm between user preferences and breed characteristics

class BreedMatcher {
    constructor(dogBreeds, catBreeds) {
        this.dogBreeds = dogBreeds;
        this.catBreeds = catBreeds;
        this.petType = null;
        this.answers = {};
        this.weights = this.getDefaultWeights();
    }

    // Set the pet type (dog or cat)
    setPetType(petType) {
        this.petType = petType;
    }

    // Set an answer for a specific question
    setAnswer(questionId, answer) {
        this.answers[questionId] = answer;
    }

    // Get default weights for different criteria
    getDefaultWeights() {
        return {
            // Common criteria
            livingSpace: 1.5,
            timeAtHome: 1.2,
            children: 1.3,
            otherPets: 1.0,
            grooming: 1.0,
            shedding: 1.2,
            activityLevel: 1.5,
            
            // Dog-specific criteria
            size: 1.4,
            training: 1.0,
            barking: 1.1,
            purpose: 1.0,
            
            // Cat-specific criteria
            personality: 1.5,
            vocalization: 1.0,
            indoorOutdoor: 1.2
        };
    }

    // Get the breeds to match against based on pet type
    getBreeds() {
        return this.petType === 'dog' ? this.dogBreeds : this.catBreeds;
    }

    // Calculate match score for a specific breed
    calculateMatchScore(breed) {
        let totalScore = 0;
        let maxPossibleScore = 0;
        
        // Calculate score based on living space
        if (this.answers.livingSpace && breed.living_space_suitability) {
            const weight = this.weights.livingSpace;
            maxPossibleScore += 10 * weight;
            
            const livingSpaceMap = {
                'apartment-no-yard': ['apartment', 'small space'],
                'apartment-small-yard': ['apartment', 'small yard', 'small space'],
                'house-medium-yard': ['house', 'medium yard', 'medium space'],
                'house-large-yard': ['house', 'large yard', 'large space', 'farm']
            };
            
            const userPreference = this.answers.livingSpace;
            const breedSuitability = breed.living_space_suitability.toLowerCase();
            
            if (livingSpaceMap[userPreference].some(space => breedSuitability.includes(space))) {
                totalScore += 10 * weight;
            } else if (userPreference === 'house-large-yard') {
                // Most breeds can adapt to larger spaces
                totalScore += 8 * weight;
            } else if (userPreference === 'house-medium-yard' && 
                      (breedSuitability.includes('large') || breedSuitability.includes('house'))) {
                totalScore += 7 * weight;
            } else {
                // Partial match
                totalScore += 5 * weight;
            }
        }
        
        // Calculate score based on time at home
        if (this.answers.timeAtHome && breed.independence_level) {
            const weight = this.weights.timeAtHome;
            maxPossibleScore += 10 * weight;
            
            const timeAtHomeMap = {
                'most-of-time': ['low', 'very low'],  // Low independence = needs someone home
                'part-of-day': ['moderate', 'medium'],
                'long-periods': ['high', 'very high']
            };
            
            const userPreference = this.answers.timeAtHome;
            const breedIndependence = breed.independence_level.toLowerCase();
            
            if (timeAtHomeMap[userPreference].some(level => breedIndependence.includes(level))) {
                totalScore += 10 * weight;
            } else if (userPreference === 'most-of-time') {
                // Any breed can adapt to more attention
                totalScore += 9 * weight;
            } else if (userPreference === 'part-of-day' && 
                      (breedIndependence.includes('low') || breedIndependence.includes('high'))) {
                totalScore += 7 * weight;
            } else {
                // Partial match
                totalScore += 4 * weight;
            }
        }
        
        // Calculate score based on children
        if (this.answers.children && breed.good_with_children) {
            const weight = this.weights.children;
            maxPossibleScore += 10 * weight;
            
            const childrenMap = {
                'no-children': true,  // Any breed is fine if no children
                'older-children': ['yes', 'good', 'excellent', 'older children'],
                'young-children': ['yes', 'good', 'excellent', 'young children'],
                'planning-children': ['yes', 'good', 'excellent']
            };
            
            const userPreference = this.answers.children;
            const breedWithChildren = breed.good_with_children.toLowerCase();
            
            if (userPreference === 'no-children') {
                totalScore += 10 * weight;
            } else if (childrenMap[userPreference].some(suitability => breedWithChildren.includes(suitability))) {
                totalScore += 10 * weight;
            } else if (breedWithChildren.includes('moderate') || breedWithChildren.includes('ok')) {
                totalScore += 6 * weight;
            } else {
                totalScore += 2 * weight;
            }
        }
        
        // Calculate score based on other pets
        if (this.answers.otherPets && breed.good_with_other_pets) {
            const weight = this.weights.otherPets;
            maxPossibleScore += 10 * weight;
            
            const otherPetsMap = {
                'no-other-pets': true,  // Any breed is fine if no other pets
                'dogs-cats': ['yes', 'good', 'excellent', 'dogs', 'cats'],
                'small-pets': ['yes', 'good', 'excellent', 'small animals'],
                'various-pets': ['yes', 'good', 'excellent']
            };
            
            const userPreference = this.answers.otherPets;
            const breedWithPets = breed.good_with_other_pets.toLowerCase();
            
            if (userPreference === 'no-other-pets') {
                totalScore += 10 * weight;
            } else if (otherPetsMap[userPreference].some(suitability => breedWithPets.includes(suitability))) {
                totalScore += 10 * weight;
            } else if (breedWithPets.includes('moderate') || breedWithPets.includes('ok')) {
                totalScore += 6 * weight;
            } else {
                totalScore += 2 * weight;
            }
        }
        
        // Calculate score based on grooming
        if (this.answers.grooming && breed.grooming_needs) {
            const weight = this.weights.grooming;
            maxPossibleScore += 10 * weight;
            
            const groomingMap = {
                'minimal': ['low', 'minimal', 'easy'],
                'moderate': ['moderate', 'medium', 'average'],
                'high': ['high', 'intensive', 'professional']
            };
            
            const userPreference = this.answers.grooming;
            const breedGrooming = breed.grooming_needs.toLowerCase();
            
            if (groomingMap[userPreference].some(level => breedGrooming.includes(level))) {
                totalScore += 10 * weight;
            } else if (userPreference === 'high') {
                // If user is willing to do high grooming, any breed is fine
                totalScore += 10 * weight;
            } else if (userPreference === 'moderate' && breedGrooming.includes('low')) {
                // If user is willing to do moderate grooming, low grooming breeds are fine
                totalScore += 10 * weight;
            } else if (userPreference === 'moderate' && breedGrooming.includes('high')) {
                // If user is willing to do moderate grooming but breed needs high
                totalScore += 5 * weight;
            } else if (userPreference === 'minimal' && breedGrooming.includes('moderate')) {
                // If user wants minimal grooming but breed needs moderate
                totalScore += 5 * weight;
            } else if (userPreference === 'minimal' && breedGrooming.includes('high')) {
                // If user wants minimal grooming but breed needs high
                totalScore += 2 * weight;
            }
        }
        
        // Calculate score based on shedding
        if (this.answers.shedding && breed.shedding) {
            const weight = this.weights.shedding;
            maxPossibleScore += 10 * weight;
            
            const sheddingMap = {
                'minimal': ['low', 'minimal', 'none', 'hypoallergenic'],
                'moderate': ['moderate', 'medium', 'average'],
                'any-shedding': true  // Any shedding level is fine
            };
            
            const userPreference = this.answers.shedding;
            const breedShedding = breed.shedding.toLowerCase();
            
            if (userPreference === 'any-shedding') {
                totalScore += 10 * weight;
            } else if (sheddingMap[userPreference].some(level => breedShedding.includes(level))) {
                totalScore += 10 * weight;
            } else if (userPreference === 'moderate' && breedShedding.includes('low')) {
                // If user is okay with moderate shedding, low shedding breeds are fine
                totalScore += 10 * weight;
            } else if (userPreference === 'moderate' && breedShedding.includes('high')) {
                // If user is okay with moderate shedding but breed has high shedding
                totalScore += 5 * weight;
            } else if (userPreference === 'minimal' && breedShedding.includes('moderate')) {
                // If user wants minimal shedding but breed has moderate shedding
                totalScore += 4 * weight;
            } else if (userPreference === 'minimal' && breedShedding.includes('high')) {
                // If user wants minimal shedding but breed has high shedding
                totalScore += 1 * weight;
            }
        }
        
        // Calculate score based on activity level
        if (this.answers.activityLevel && breed.energy_level) {
            const weight = this.weights.activityLevel;
            maxPossibleScore += 10 * weight;
            
            const activityMap = {
                'sedentary': ['low', 'very low', 'calm', 'relaxed'],
                'moderately-active': ['moderate', 'medium', 'average'],
                'active': ['high', 'energetic', 'active'],
                'very-active': ['very high', 'very energetic', 'very active']
            };
            
            const userPreference = this.answers.activityLevel;
            const breedEnergy = breed.energy_level.toLowerCase();
            
            if (activityMap[userPreference].some(level => breedEnergy.includes(level))) {
                totalScore += 10 * weight;
            } else if (userPreference === 'very-active' && breedEnergy.includes('high')) {
                // If user is very active, high energy breeds are good too
                totalScore += 9 * weight;
            } else if (userPreference === 'active' && breedEnergy.includes('very high')) {
                // If user is active, very high energy breeds might be too much
                totalScore += 7 * weight;
            } else if (userPreference === 'active' && breedEnergy.includes('moderate')) {
                // If user is active but breed has moderate energy
                totalScore += 6 * weight;
            } else if (userPreference === 'moderately-active' && breedEnergy.includes('high')) {
                // If user is moderately active but breed has high energy
                totalScore += 5 * weight;
            } else if (userPreference === 'moderately-active' && breedEnergy.includes('low')) {
                // If user is moderately active but breed has low energy
                totalScore += 7 * weight;
            } else if (userPreference === 'sedentary' && breedEnergy.includes('moderate')) {
                // If user is sedentary but breed has moderate energy
                totalScore += 5 * weight;
            } else if (userPreference === 'sedentary' && (breedEnergy.includes('high') || breedEnergy.includes('very high'))) {
                // If user is sedentary but breed has high energy
                totalScore += 2 * weight;
            }
        }
        
        // Dog-specific criteria
        if (this.petType === 'dog') {
            // Calculate score based on size
            if (this.answers.size && breed.size) {
                const weight = this.weights.size;
                maxPossibleScore += 10 * weight;
                
                const sizeMap = {
                    'small': ['small', 'toy'],
                    'medium': ['medium'],
                    'large': ['large'],
                    'giant': ['giant', 'very large', 'extra large'],
                    'no-preference': true  // Any size is fine
                };
                
                const userPreference = this.answers.size;
                const breedSize = breed.size.toLowerCase();
                
                if (userPreference === 'no-preference') {
                    totalScore += 10 * weight;
                } else if (sizeMap[userPreference].some(size => breedSize.includes(size))) {
                    totalScore += 10 * weight;
                } else {
                    // Calculate partial match based on how far off the size is
                    const sizeOrder = ['small', 'medium', 'large', 'giant'];
                    const userIndex = sizeOrder.indexOf(userPreference);
                    
                    let breedIndex = -1;
                    for (let i = 0; i < sizeOrder.length; i++) {
                        if (breedSize.includes(sizeOrder[i])) {
                            breedIndex = i;
                            break;
                        }
                    }
                    
                    if (breedIndex !== -1) {
                        const difference = Math.abs(userIndex - breedIndex);
                        if (difference === 1) {
                            totalScore += 6 * weight;  // Adjacent size
                        } else if (difference === 2) {
                            totalScore += 3 * weight;  // Two sizes away
                        } else {
                            totalScore += 1 * weight;  // Three sizes away
                        }
                    } else {
                        totalScore += 1 * weight;  // Unknown size
                    }
                }
            }
            
            // Calculate score based on training
            if (this.answers.training && breed.trainability) {
                const weight = this.weights.training;
                maxPossibleScore += 10 * weight;
                
                const trainingMap = {
                    'basic': ['easy', 'very easy', 'good', 'excellent'],
                    'regular': ['moderate', 'good', 'excellent'],
                    'extensive': ['good', 'excellent', 'intelligent', 'highly trainable'],
                    'professional': true  // Any trainability is fine with professional help
                };
                
                const userPreference = this.answers.training;
                const breedTrainability = breed.trainability.toLowerCase();
                
                if (userPreference === 'professional') {
                    totalScore += 10 * weight;
                } else if (trainingMap[userPreference].some(level => breedTrainability.includes(level))) {
                    totalScore += 10 * weight;
                } else if (userPreference === 'basic' && breedTrainability.includes('difficult')) {
                    totalScore += 3 * weight;
                } else if (userPreference === 'regular' && breedTrainability.includes('difficult')) {
                    totalScore += 5 * weight;
                } else if (userPreference === 'extensive' && breedTrainability.includes('difficult')) {
                    totalScore += 6 * weight;
                } else {
                    totalScore += 5 * weight;  // Partial match
                }
            }
            
            // Calculate score based on barking
            if (this.answers.barking && breed.barking_tendency) {
                const weight = this.weights.barking;
                maxPossibleScore += 10 * weight;
                
                const barkingMap = {
                    'quiet': ['low', 'minimal', 'quiet', 'rarely'],
                    'occasional': ['moderate', 'medium', 'average', 'occasionally'],
                    'any-barking': true  // Any barking level is fine
                };
                
                const userPreference = this.answers.barking;
                const breedBarking = breed.barking_tendency.toLowerCase();
                
                if (userPreference === 'any-barking') {
                    totalScore += 10 * weight;
                } else if (barkingMap[userPreference].some(level => breedBarking.includes(level))) {
                    totalScore += 10 * weight;
                } else if (userPreference === 'occasional' && breedBarking.includes('low')) {
                    totalScore += 10 * weight;
                } else if (userPreference === 'occasional' && breedBarking.includes('high')) {
                    totalScore += 5 * weight;
                } else if (userPreference === 'quiet' && breedBarking.includes('moderate')) {
                    totalScore += 5 * weight;
                } else if (userPreference === 'quiet' && breedBarking.includes('high')) {
                    totalScore += 2 * weight;
                }
            }
            
            // Calculate score based on purpose
            if (this.answers.purpose && breed.temperament) {
                const weight = this.weights.purpose;
                maxPossibleScore += 10 * weight;
                
                const purposeMap = {
                    'companionship': ['affectionate', 'friendly', 'loyal', 'gentle', 'loving'],
                    'family': ['friendly', 'gentle', 'patient', 'good with children', 'playful'],
                    'protection': ['protective', 'alert', 'watchful', 'territorial', 'guard'],
                    'active': ['energetic', 'athletic', 'agile', 'active', 'playful']
                };
                
                const userPreference = this.answers.purpose;
                const breedTemperament = breed.temperament.toLowerCase();
                
                let matchCount = 0;
                const traits = purposeMap[userPreference];
                
                for (const trait of traits) {
                    if (breedTemperament.includes(trait)) {
                        matchCount++;
                    }
                }
                
                const matchPercentage = matchCount / traits.length;
                totalScore += (matchPercentage * 10) * weight;
            }
        }
        
        // Cat-specific criteria
        if (this.petType === 'cat') {
            // Calculate score based on personality
            if (this.answers.personality && breed.temperament) {
                const weight = this.weights.personality;
                maxPossibleScore += 10 * weight;
                
                const personalityMap = {
                    'independent': ['independent', 'reserved', 'quiet', 'calm'],
                    'balanced': ['balanced', 'moderate', 'adaptable'],
                    'affectionate': ['affectionate', 'loving', 'cuddly', 'lap cat'],
                    'playful': ['playful', 'active', 'energetic', 'curious']
                };
                
                const userPreference = this.answers.personality;
                const breedTemperament = breed.temperament.toLowerCase();
                
                let matchCount = 0;
                const traits = personalityMap[userPreference];
                
                for (const trait of traits) {
                    if (breedTemperament.includes(trait)) {
                        matchCount++;
                    }
                }
                
                const matchPercentage = matchCount / traits.length;
                totalScore += (matchPercentage * 10) * weight;
            }
            
            // Calculate score based on vocalization
            if (this.answers.vocalization && breed.vocalization) {
                const weight = this.weights.vocalization;
                maxPossibleScore += 10 * weight;
                
                const vocalizationMap = {
                    'quiet': ['quiet', 'silent', 'rarely', 'low'],
                    'moderate': ['moderate', 'occasionally', 'medium'],
                    'talkative': ['talkative', 'vocal', 'chatty', 'high']
                };
                
                const userPreference = this.answers.vocalization;
                const breedVocalization = breed.vocalization.toLowerCase();
                
                if (vocalizationMap[userPreference].some(level => breedVocalization.includes(level))) {
                    totalScore += 10 * weight;
                } else if (userPreference === 'talkative' && breedVocalization.includes('moderate')) {
                    totalScore += 7 * weight;
                } else if (userPreference === 'moderate' && breedVocalization.includes('quiet')) {
                    totalScore += 8 * weight;
                } else if (userPreference === 'moderate' && breedVocalization.includes('talkative')) {
                    totalScore += 6 * weight;
                } else if (userPreference === 'quiet' && breedVocalization.includes('moderate')) {
                    totalScore += 5 * weight;
                } else if (userPreference === 'quiet' && breedVocalization.includes('talkative')) {
                    totalScore += 2 * weight;
                }
            }
            
            // Calculate score based on indoor/outdoor preference
            if (this.answers.indoorOutdoor && breed.indoor_outdoor_preference) {
                const weight = this.weights.indoorOutdoor;
                maxPossibleScore += 10 * weight;
                
                const indoorOutdoorMap = {
                    'indoor': ['indoor', 'indoor only'],
                    'mostly-indoor': ['indoor', 'mostly indoor'],
                    'indoor-outdoor': ['both', 'indoor-outdoor', 'adaptable'],
                    'mostly-outdoor': ['outdoor', 'mostly outdoor']
                };
                
                const userPreference = this.answers.indoorOutdoor;
                const breedPreference = breed.indoor_outdoor_preference.toLowerCase();
                
                if (indoorOutdoorMap[userPreference].some(pref => breedPreference.includes(pref))) {
                    totalScore += 10 * weight;
                } else if (userPreference === 'indoor' && breedPreference.includes('both')) {
                    totalScore += 9 * weight;
                } else if (userPreference === 'mostly-indoor' && breedPreference.includes('both')) {
                    totalScore += 9 * weight;
                } else if (userPreference === 'indoor-outdoor' && 
                          (breedPreference.includes('indoor') || breedPreference.includes('outdoor'))) {
                    totalScore += 7 * weight;
                } else if (userPreference === 'mostly-outdoor' && breedPreference.includes('both')) {
                    totalScore += 8 * weight;
                } else if (userPreference === 'mostly-outdoor' && breedPreference.includes('indoor')) {
                    totalScore += 4 * weight;
                } else if (userPreference === 'indoor' && breedPreference.includes('outdoor')) {
                    totalScore += 3 * weight;
                }
            }
        }
        
        // Calculate final percentage score
        const percentageScore = Math.round((totalScore / maxPossibleScore) * 100);
        return Math.min(percentageScore, 100);  // Cap at 100%
    }

    // Get top matching breeds
    getTopMatches(count = 5) {
        const breeds = this.getBreeds();
        
        // Calculate match scores for all breeds
        const matchedBreeds = breeds.map(breed => {
            return {
                ...breed,
                matchScore: this.calculateMatchScore(breed)
            };
        });
        
        // Sort by match score (descending)
        matchedBreeds.sort((a, b) => b.matchScore - a.matchScore);
        
        // Return top matches
        return matchedBreeds.slice(0, count);
    }
}

export default BreedMatcher;
